'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LoadingContextType {
  registerLoader: (id: string) => void;
  markLoaded: (id: string) => void;
  isLoading: boolean;
}

const LoadingContext = createContext<LoadingContextType>({
  registerLoader: () => {},
  markLoaded: () => {},
  isLoading: false, // Start with false - show content immediately by default
});

export const usePageLoading = () => useContext(LoadingContext);

interface PageLoadingProviderProps {
  children: ReactNode;
}

export const PageLoadingProvider = ({ children }: PageLoadingProviderProps) => {
  const [loadingComponents, setLoadingComponents] = useState<Set<string>>(new Set());
  // Start with false - no loading screen unless something explicitly registers
  const [isLoading, setIsLoading] = useState(false);

  const registerLoader = (id: string) => {
    setLoadingComponents((prev) => {
      const next = new Set(prev).add(id);
      // Only show loading if something registers
      if (next.size > 0) setIsLoading(true);
      return next;
    });
  };

  const markLoaded = (id: string) => {
    setLoadingComponents((prev) => {
      const next = new Set(prev);
      next.delete(id);
      // Hide loading when all components are done
      if (next.size === 0) setIsLoading(false);
      return next;
    });
  };

  return (
    <LoadingContext.Provider value={{ registerLoader, markLoaded, isLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};

